"use client";
import { cn } from "@/lib";
import { FlexCol } from "../ui";
import { IHistoryItemData, TVariantType } from "@/interfaces";
import { usePathname } from "next/navigation";
import { useAppStore } from "@/store";
import { useTranslations } from "next-intl";
import { BdAltArrowLeft } from "solar-icon-react/bd";
import Link from "next/link";

const variantClasses = {
  "emerald-mint": "bg-emerald-mint",
  "sky-bliss": "bg-sky-bliss",
  "sunset-gold": "bg-sunset-gold",
  "teal-ocean": "bg-teal-ocean",
  "yellow-500": "bg-yellow-500",
  "rose-500": "bg-rose-500",
} as const;

interface HistoryItemProps extends IHistoryItemData {
  href: string;
  linkTitle: string;
  variant: TVariantType;
  status: boolean;
  hasData: boolean;
  message?: string | null;
  inquiryFlag?: string;
}

const HistoryItem: React.FC<HistoryItemProps> = ({
  date,
  title,
  href,
  linkTitle,
  categoryIcon,
  variant,
  input,
  status,
  message,
  inquiryFlag,
  hasData,
  code,
}) => {
  const t = useTranslations();
  const pathName = usePathname();
  const { setPreviousPath } = useAppStore();
  return (
    <article
      className={cn(
        "flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-around sm:gap-4 xl:gap-10",
      )}
    >
      <time className={cn("text-micro sm:text-xs md:text-sm lg:text-base")}>
        {date}
      </time>
      <div
        className={cn(
          "shadow-main flex grow justify-between rounded-xl bg-white",
        )}
      >
        <div
          className={cn(
            variantClasses[variant],
            "basis-[20px] rounded-s-xl lg:min-h-[120px] lg:basis-[30px]",
          )}
        />
        <FlexCol
          className={cn("w-full justify-between gap-2 p-2 lg:gap-4 lg:p-4")}
        >
          <header className={cn("flex w-full justify-between")}>
            <div className={cn("flex justify-between gap-2")}>
              <div
                className={cn(
                  "flex items-center justify-center",
                  "w-4 sm:w-5 md:w-[23px] lg:w-[25px]",
                )}
              >
                {categoryIcon}
              </div>
              <h2
                className={cn(
                  "flex items-center justify-center",
                  "text-micro sm:text-xs md:text-sm lg:text-base",
                )}
              >
                {title}
              </h2>
            </div>
            {hasData ? (
              <div>
                {!status ? (
                  <div
                    className={cn(
                      "rounded-lg bg-red-200 px-2 py-1 text-sm text-red-500",
                    )}
                  >
                    ناموفق
                  </div>
                ) : (
                  <Link
                    id={`inquiry-history-view-${inquiryFlag}`}
                    href={href}
                    title={linkTitle}
                    className={cn(
                      "flex size-full items-center justify-between md:gap-2",
                    )}
                    onClick={() => {
                      setPreviousPath(pathName);
                    }}
                  >
                    <h2
                      className={cn(
                        "text-primary-main! flex items-center justify-center",
                        "text-micro sm:text-xs md:text-sm lg:text-base",
                      )}
                    >
                      مشاهده نتیجه
                    </h2>
                    <span
                      className={cn(
                        "flex items-center justify-center",
                        "w-4 sm:w-5 md:w-[23px] lg:w-[25px]",
                      )}
                    >
                      <BdAltArrowLeft
                        size={30}
                        className={cn("text-primary-main! size-full!")}
                      />
                    </span>
                  </Link>
                )}
              </div>
            ) : (
              <>
                {code && (
                  <div
                    className={cn("flex items-center gap-1 text-xs md:text-sm")}
                  >
                    <span className="text-gray-500">کد اصالت : </span>
                    <span className="text-primary-main text-en">{code}</span>
                  </div>
                )}
              </>
            )}
          </header>
          <hr
            className={cn(
              "border-custom-gray-500/50 w-full rounded-xl border-t-2",
            )}
          />
          <div className={cn("flex w-full")}>
            {!status ? (
              <p
                className={cn(
                  "text-custom-gray-200",
                  "text-micro sm:text-xs md:text-sm lg:text-base",
                )}
              >
                {message ?? "خطا در استعلام"}
              </p>
            ) : (
              <p
                className={cn(
                  "text-custom-gray-200",
                  "text-micro sm:text-xs md:text-sm lg:text-base",
                )}
              >
                {input ?? ""}
              </p>
            )}
          </div>
        </FlexCol>
      </div>
    </article>
  );
};

export default HistoryItem;
